import { create } from 'zustand';
import { authService } from '../services/auth.service';
import { useWorkspaceStore } from './workspace.store';

export interface WorkspacePermissions {
  can_view_groups: boolean;
  can_view_teams: boolean;
  can_view_sprints: boolean;
  can_view_goals: boolean;
  can_view_tasks: boolean;
  can_view_performance: boolean;
  can_view_reports: boolean;
}

export interface UserWorkspace {
  id: string;
  name: string;
  logo_url: string | null;
  role: string;
  permissions: WorkspacePermissions;
}

export interface User {
  id: string;
  email: string;
  full_name: string;
  avatar_url: string | null;
  app_role: string;
  approval_status: string;
  email_verified: boolean;
  working_days?: string[];
  daily_hours?: number;
  timezone?: string;
  workspaces?: UserWorkspace[];
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  logout: () => void;
  initialize: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, _get) => ({
  user: null,
  accessToken: localStorage.getItem('access_token'),
  isAuthenticated: !!localStorage.getItem('access_token'),
  // Only loading if there's a token to validate. No token = definitely not authenticated.
  isLoading: !!localStorage.getItem('access_token'),

  setUser: (user) => {
    set({ user, isAuthenticated: !!user, isLoading: false });

    // Sync workspaces to workspace store if available
    if (user?.workspaces && user.workspaces.length > 0) {
      const wsStore = useWorkspaceStore.getState();
      wsStore.setWorkspaces(
        user.workspaces.map((w) => ({
          id: w.id,
          name: w.name,
          description: null,
          logo_url: w.logo_url,
          role: w.role,
          members_count: 0,
        }))
      );
      // Auto-select if none active
      if (!wsStore.activeWorkspace) {
        const savedId = localStorage.getItem('active_workspace_id');
        const saved = user.workspaces.find((w) => w.id === savedId);
        wsStore.setActiveWorkspace(
          saved
            ? { id: saved.id, name: saved.name, description: null, logo_url: saved.logo_url, role: saved.role, members_count: 0 }
            : { id: user.workspaces[0].id, name: user.workspaces[0].name, description: null, logo_url: user.workspaces[0].logo_url, role: user.workspaces[0].role, members_count: 0 }
        );
      }
    }
  },

  setToken: (token) => {
    if (token) {
      localStorage.setItem('access_token', token);
    } else {
      localStorage.removeItem('access_token');
    }
    set({ accessToken: token, isAuthenticated: !!token, isLoading: false });
  },

  logout: () => {
    authService.logout().catch(() => {});
    localStorage.removeItem('access_token');
    localStorage.removeItem('active_workspace_id');
    set({ user: null, accessToken: null, isAuthenticated: false, isLoading: false });
    // Full page reload to /login — guarantees clean React state, empty query cache, no stale closures
    window.location.href = '/login';
  },

  initialize: async () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      set({ isLoading: false, isAuthenticated: false });
      return;
    }
    try {
      const { data } = await authService.getMe();
      const user = data.data;
      set({ user, isAuthenticated: true, isLoading: false });

      // Sync workspaces
      if (user?.workspaces && user.workspaces.length > 0) {
        const wsStore = useWorkspaceStore.getState();
        wsStore.setWorkspaces(
          user.workspaces.map((w: UserWorkspace) => ({
            id: w.id,
            name: w.name,
            description: null,
            logo_url: w.logo_url,
            role: w.role,
            members_count: 0,
          }))
        );
        if (!wsStore.activeWorkspace) {
          const savedId = localStorage.getItem('active_workspace_id');
          const saved = user.workspaces.find((w: UserWorkspace) => w.id === savedId);
          const target = saved || user.workspaces[0];
          wsStore.setActiveWorkspace({
            id: target.id,
            name: target.name,
            description: null,
            logo_url: target.logo_url,
            role: target.role,
            members_count: 0,
          });
        }
      }
    } catch {
      localStorage.removeItem('access_token');
      set({ user: null, accessToken: null, isAuthenticated: false, isLoading: false });
    }
  },
}));
